<template>
  <VKanbanBoardList>
    <template #header>
      <div class="flex justify-between items-center">
        <h2 class="text-sm font-medium">
          {{ productionOrderItemStatus }}
        </h2>
        <span
          v-if="meta"
          class="bg-gray-400 text-gray-900 inline-block px-2 text-xs font-normal rounded-full"
        >
          {{ meta.total }}
        </span>
      </div>
    </template>

    <template #default v-if="ids.length">
      <KanbanBoardProductionOrderItemListLine
        v-for="id in ids"
        :key="id"
        :id="id"
        :last-edited-production-order-item-id="lastEditedProductionOrderItemId"
        @production-order-item:view="$emit('production-order-item:view', $event)"
        @production-order-item:updated="notifyProductionOrderItemUpdated"
      />
      <BaseButton v-if="isIdle && hasMore" primary block> Load more </BaseButton>
    </template>

    <template #default v-else>
      <div class="p-4 italic text-center text-sm my-2">No Production orders</div>
    </template>
  </VKanbanBoardList>
</template>

<script>
import VKanbanBoardList from '@/components/Common/VKanbanBoardList.vue';
import KanbanBoardProductionOrderItemListLine from './KanbanBoardProductionOrderItemListLine.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

const includes = ['order_items', 'provider', 'project'].join(',');

export default {
  name: 'ProductionOrderList',

  components: {
    VKanbanBoardList,
    KanbanBoardProductionOrderItemListLine,
  },

  props: {
    productionOrderItemStatus: {
      type: String,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    lastEditedProductionOrderItemId: {
      type: String,
      default: null,
    },
    fetchProductionOrders: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      status: STATUS_IDLE,
      ids: [],
      meta: null,
    };
  },

  computed: {
    isIdle() {
      return this.status === STATUS_IDLE;
    },
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
    hasMore() {
      return this.meta.current_page < this.meta.last_page;
    },
  },

  watch: {
    filters() {
      this.refresh();
    },
    meta(newMeta) {
      const { current_page: curPage = 1, last_page: lastPage = 1 } = newMeta || {};

      if (curPage === lastPage) {
        return;
      }

      if (curPage === 1) {
        this.fetchData(2);
      }
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    async fetchData(page = 1, filters = {}) {
      this.status = STATUS_PENDING;

      try {
        const {
          data: { data, meta },
        } = await this.fetchProductionOrders({
          params: {
            page,
            include: includes,
            filter: {
              ...filters,
              'order_items.status': this.productionOrderItemStatus,
            },
          },
        });

        await this.persistData(data);

        this.meta = meta;

        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;
      }
    },

    async persistData(data) {
      try {
        const ids = await this.$store.dispatch('entities/persistEntities', {
          type: 'orders',
          data,
        });

        this.ids = [...this.ids, ...ids];
      } catch (error) {
        /** handle error */
      }
    },

    refresh() {
      this.ids = [];
      this.fetchData(1, this.filters);
    },

    notifyProductionOrderItemUpdated({ productionOrderItem, oldProductionOrderItem }) {
      if (productionOrderItem.status !== oldProductionOrderItem.status) {
        this.$emit(
          'production-order-list:updated',
          [productionOrderItem.status, oldProductionOrderItem.status].filter((status) => status)
        );
      }
    },
  },
};
</script>
